import * as Popover from '@radix-ui/react-popover';
import * as Select from '@radix-ui/react-select';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import React, { useState } from 'react';
import { cn } from '../../../lib/utils';

interface ModernDatePickerProps {
  selected?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  calendarType?: 'start-date' | 'end-date';
}

const ModernDatePicker: React.FC<ModernDatePickerProps> = ({
  selected,
  onChange,
  placeholder = 'Select date',
  minDate,
  maxDate,
  className,
  align = 'start',
  side = 'bottom',
  calendarType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(selected || new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // Get the start and end of the calendar grid (includes previous/next month days to fill the grid)
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // 0 = Sunday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const handleDateClick = (date: Date) => {
    onChange?.(date);
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const handleYearChange = (year: string) => {
    setCurrentMonth(prev => setYear(prev, parseInt(year)));
  };

  const handleMonthChange = (monthIndex: string) => {
    setCurrentMonth(prev => setMonth(prev, parseInt(monthIndex)));
  };

  const isDateDisabled = (date: Date) => {
    if (minDate && isBefore(date, minDate)) return true;
    if (maxDate && isAfter(date, maxDate)) return true;

    return false;
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const currentYear = currentMonth.getFullYear();
  const currentMonthIndex = currentMonth.getMonth();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - 15 + i);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <motion.button
          className={cn(
            'flex items-center gap-2 px-3 py-2 text-xs font-medium',
            'bg-white/25 backdrop-blur-sm border border-white/40 rounded-lg',
            'text-white placeholder-white/70 transition-all duration-300',
            'hover:bg-white/30 hover:border-white/50',
            'focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 focus:outline-none',
            'w-32 min-h-[32px]',
            className
          )}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <CalendarDays className="w-3 h-3 text-white/80" />
          <span className="truncate text-xs">
            {selected ? format(selected, 'MMM dd, yyyy') : placeholder}
          </span>
        </motion.button>
      </Popover.Trigger>

      <AnimatePresence>
        {isOpen && (
          <Popover.Portal>
            <Popover.Content
              className="fixed z-[999999] outline-none"
              sideOffset={8}
              side={side}
              align={align}
              collisionPadding={20}
              avoidCollisions={true}
              data-calendar-type={calendarType}
              onOpenAutoFocus={e => e.preventDefault()}
              onCloseAutoFocus={e => e.preventDefault()}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="bg-white/95 backdrop-blur-xl border-4 border-emerald-500/30 rounded-2xl shadow-2xl overflow-hidden w-80"
              >
                {/* Calendar Header */}
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 border-b border-emerald-200/50">
                  <div className="flex items-center justify-between mb-4">
                    <motion.button
                      onClick={handlePrevMonth}
                      className="p-2 rounded-lg bg-white/80 hover:bg-emerald-50 transition-colors shadow-sm"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronLeft className="w-4 h-4 text-emerald-600" />
                    </motion.button>

                    {/* Month and Year Dropdowns */}
                    <div className="flex items-center gap-2">
                      {/* Month Selector */}
                      <Select.Root
                        value={currentMonthIndex.toString()}
                        onValueChange={handleMonthChange}
                      >
                        <Select.Trigger className="flex items-center gap-1 px-3 py-1.5 bg-white/80 rounded-lg border border-emerald-200 hover:bg-emerald-50 transition-colors focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-sm">
                          <Select.Value />
                          <ChevronDown className="w-3 h-3 text-emerald-600" />
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content className="bg-white rounded-lg border border-emerald-200 shadow-xl z-[999999] max-h-60 overflow-hidden">
                            <Select.ScrollUpButton className="flex items-center justify-center h-6 bg-white text-emerald-600 cursor-default">
                              <ChevronUp className="w-4 h-4" />
                            </Select.ScrollUpButton>
                            <Select.Viewport className="p-1">
                              {months.map((month, index) => (
                                <Select.Item
                                  key={index}
                                  value={index.toString()}
                                  className="flex items-center px-3 py-2 text-sm text-slate-700 rounded-md hover:bg-emerald-50 cursor-pointer focus:bg-emerald-100 focus:outline-none data-[highlighted]:bg-emerald-50"
                                >
                                  <Select.ItemText>{month}</Select.ItemText>
                                </Select.Item>
                              ))}
                            </Select.Viewport>
                            <Select.ScrollDownButton className="flex items-center justify-center h-6 bg-white text-emerald-600 cursor-default">
                              <ChevronDown className="w-4 h-4" />
                            </Select.ScrollDownButton>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>

                      {/* Year Selector */}
                      <Select.Root value={currentYear.toString()} onValueChange={handleYearChange}>
                        <Select.Trigger className="flex items-center gap-1 px-3 py-1.5 bg-white/80 rounded-lg border border-emerald-200 hover:bg-emerald-50 transition-colors focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-sm">
                          <Select.Value />
                          <ChevronDown className="w-3 h-3 text-emerald-600" />
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content className="bg-white rounded-lg border border-emerald-200 shadow-xl z-[999999] max-h-60 overflow-hidden">
                            <Select.ScrollUpButton className="flex items-center justify-center h-6 bg-white text-emerald-600 cursor-default">
                              <ChevronUp className="w-4 h-4" />
                            </Select.ScrollUpButton>
                            <Select.Viewport className="p-1">
                              {years.map(year => (
                                <Select.Item
                                  key={year}
                                  value={year.toString()}
                                  className="flex items-center px-3 py-2 text-sm text-slate-700 rounded-md hover:bg-emerald-50 cursor-pointer focus:bg-emerald-100 focus:outline-none data-[highlighted]:bg-emerald-50"
                                >
                                  <Select.ItemText>{year}</Select.ItemText>
                                </Select.Item>
                              ))}
                            </Select.Viewport>
                            <Select.ScrollDownButton className="flex items-center justify-center h-6 bg-white text-emerald-600 cursor-default">
                              <ChevronDown className="w-4 h-4" />
                            </Select.ScrollDownButton>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </div>

                    <motion.button
                      onClick={handleNextMonth}
                      className="p-2 rounded-lg bg-white/80 hover:bg-emerald-50 transition-colors shadow-sm"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronRight className="w-4 h-4 text-emerald-600" />
                    </motion.button>
                  </div>

                  {/* Days of Week */}
                  <div className="grid grid-cols-7 gap-1">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div
                        key={day}
                        className="h-8 flex items-center justify-center text-xs font-semibold text-emerald-700"
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="p-4">
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((day, index) => {
                      const isSelectedDay = selected && isSameDay(day, selected);
                      const isToday_ = isToday(day);
                      const isDisabled = isDateDisabled(day);
                      const isCurrentMonth = isSameMonth(day, currentMonth);

                      return (
                        <motion.button
                          key={index}
                          onClick={() => !isDisabled && handleDateClick(day)}
                          disabled={isDisabled}
                          className={cn(
                            'h-9 w-9 text-sm font-medium rounded-lg transition-all duration-200',
                            'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-emerald-400',
                            {
                              // Selected day
                              'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg scale-105':
                                isSelectedDay,
                              // Today (not selected)
                              'bg-emerald-100 text-emerald-800 ring-2 ring-emerald-300 font-bold':
                                isToday_ && !isSelectedDay,
                              // Regular days in current month
                              'text-slate-700 hover:bg-emerald-50':
                                isCurrentMonth && !isSelectedDay && !isToday_ && !isDisabled,
                              // Days outside current month
                              'text-slate-400 hover:text-slate-500': !isCurrentMonth && !isDisabled,
                              // Disabled days
                              'text-slate-300 cursor-not-allowed hover:scale-100': isDisabled,
                            }
                          )}
                          whileHover={!isDisabled ? { scale: 1.05 } : {}}
                          whileTap={!isDisabled ? { scale: 0.95 } : {}}
                        >
                          {format(day, 'd')}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gradient-to-r from-slate-50 to-emerald-50 p-3 border-t border-emerald-200/50">
                  <div className="flex justify-center gap-3">
                    <motion.button
                      onClick={() => handleDateClick(new Date())}
                      className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 px-4 py-2 rounded-lg hover:bg-emerald-100 transition-colors bg-white/70 shadow-sm"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Today
                    </motion.button>
                    <motion.button
                      onClick={() => {
                        onChange?.(null);
                        setIsOpen(false);
                      }}
                      className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors bg-white/70 shadow-sm"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Clear
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
};

export default ModernDatePicker;
